import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Header, Button, Icon, Segment, Loader } from 'semantic-ui-react';
import { format, isSameDay, isSameYear } from 'date-fns';
import MessageThread from './MessageThread';
import MessageComposer from './MessageComposer';
import ThreadSelector from './ThreadSelector';
import {
  fetchConversation,
  sendMessage,
  markConversationAsRead,
  selectMessages,
  selectMessagesLoading
} from '../../../slices/messageSlice';
import { selectCurrentUser } from '../../../slices/userSlice';
import './ConversationView.css';

/**
 * Format a thread's earliest timestamp into a short, context-aware label suffix.
 * Same day  -> "2:30 PM"
 * Same year -> "Apr 14, 2:30 PM"
 * Otherwise -> "Apr 14, 2025"
 * Returns empty string when the timestamp is missing or invalid.
 */
const formatThreadTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  try {
    if (isSameDay(date, now)) return format(date, 'h:mm a');
    if (isSameYear(date, now)) return format(date, 'MMM d, h:mm a');
    return format(date, 'MMM d, yyyy');
  } catch (e) {
    return '';
  }
};

/**
 * Title-case a single token (used for unknown-prefix fallbacks).
 */
const titleCase = (token = '') =>
  token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();

/**
 * Build a human-readable name for a thread. Falls back to a pretty-printed
 * prefix + date so raw UUIDs never surface in the UI.
 *
 * @param {string} threadId     - Raw threadId from the backend (or 'general')
 * @param {string|Date} [submittedAt] - createdAt of the earliest message in the thread
 */
const getThreadName = (threadId, submittedAt) => {
  if (!threadId || threadId === 'general') return 'General Discussion';

  const stamp = formatThreadTimestamp(submittedAt);
  const withStamp = (label) => (stamp ? `${label} · ${stamp}` : label);

  // Known prefixes
  if (threadId.startsWith('recording_submission_')) return withStamp('Recording Submission');
  if (threadId.startsWith('opportunity_')) return withStamp('Opportunity Discussion');
  if (threadId.startsWith('application_')) return withStamp('Application Discussion');
  if (threadId.startsWith('variation-') || threadId.startsWith('variation_')) {
    return withStamp('Voice Variation');
  }

  // Fallback: take the prefix portion before the first UUID-looking or long token
  // and title-case it so we never leak a raw UUID.
  const separatorIdx = Math.max(threadId.indexOf('_'), threadId.indexOf('-'));
  if (separatorIdx > 0) {
    const prefix = threadId.slice(0, separatorIdx);
    const pretty = prefix
      .split(/[-_]+/)
      .filter(Boolean)
      .map(titleCase)
      .join(' ');
    return withStamp(pretty || 'Discussion');
  }

  return withStamp(titleCase(threadId));
};


const ConversationView = ({ conversation, onBack, showBackButton = false }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const loading = useSelector(selectMessagesLoading);
  const messages = useSelector(selectMessages(conversation?.conversationId));
  const [activeThreadId, setActiveThreadId] = useState(conversation?.activeThreadId || null);
  const messagesEndRef = useRef(null);

  // Extract thread information from messages
  const threads = React.useMemo(() => {
    if (!messages || messages.length === 0) return [];

    const threadMap = new Map();

    messages.forEach(message => {
      const threadId = message.threadId || 'general';
      if (!threadMap.has(threadId)) {
        threadMap.set(threadId, {
          id: threadId,
          name: threadId, // placeholder; resolved to friendly name below once we know earliest createdAt
          messages: [],
          unreadCount: 0,
          submittedAt: null
        });
      }

      const thread = threadMap.get(threadId);
      thread.messages.push(message);

      // Track the earliest message timestamp as the thread's "submitted at"
      const ts = message.createdAt ? new Date(message.createdAt).getTime() : NaN;
      if (!Number.isNaN(ts)) {
        if (thread.submittedAt == null || ts < thread.submittedAt) {
          thread.submittedAt = ts;
        }
      }

      if (!message.isRead && message.recipient?.userId === currentUser?.userId) {
        thread.unreadCount++;
      }
    });

    // Resolve friendly names now that we have the earliest timestamp per thread
    const resolved = Array.from(threadMap.values()).map(t => ({
      ...t,
      name: getThreadName(t.id, t.submittedAt)
    }));

    return resolved.sort((a, b) => {
      if (a.id === 'general') return -1;
      if (b.id === 'general') return 1;
      // Sort remaining threads by submittedAt descending (newest first) so most
      // recent submissions appear near the top. Fall back to alpha on the name.
      const aTs = a.submittedAt || 0;
      const bTs = b.submittedAt || 0;
      if (aTs !== bTs) return bTs - aTs;
      return a.name.localeCompare(b.name);
    });
  }, [messages, currentUser?.userId]);


  // Get current thread messages
  const currentThreadMessages = React.useMemo(() => {
    if (!activeThreadId) return messages || [];
    return messages?.filter(msg =>
      (msg.threadId || 'general') === activeThreadId
    ) || [];
  }, [messages, activeThreadId]);

  useEffect(() => {
    if (conversation && currentUser?.userId) {
      const otherUserId = conversation.otherParticipant.userId;
      // Always fetch the ENTIRE conversation (all threads). Thread selection is
      // a pure client-side filter handled by `currentThreadMessages` below.
      //
      // Rationale: if we re-fetch with a specific threadId whenever the user
      // clicks a thread, the Redux `messages` array gets replaced with only
      // that thread's messages. Our `threads` memo (derived from `messages`)
      // then collapses to a single entry, and the `threads.length > 1` guard
      // on the sidebar removes the ThreadSelector — leaving the user with no
      // way to pick a different thread. See prior bug report.
      //
      // Volume is low (typical conversation < ~25 messages), so fetching the
      // whole set is cheap.
      dispatch(fetchConversation({
        userId: currentUser.userId,
        otherUserId,
        threadId: null
      }));
    }
  }, [conversation, currentUser?.userId, dispatch]);


  useEffect(() => {
    // Set default active thread
    if (threads.length > 0 && !activeThreadId) {
      setActiveThreadId(threads[0].id);
    }
  }, [threads, activeThreadId]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    scrollToBottom();
  }, [currentThreadMessages]);

  useEffect(() => {
    // Mark conversation as read when viewing
    if (conversation && currentUser?.userId) {
      const timer = setTimeout(() => {
        dispatch(markConversationAsRead({
          userId: currentUser.userId,
          otherUserId: conversation.otherParticipant.userId
        }));
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [conversation, currentUser?.userId, dispatch]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (content, messageType = 'GENERAL', relatedEntityId = null) => {
    if (!conversation || !currentUser?.userId || !content.trim()) return;

    const messageData = {
      senderId: currentUser.userId,
      recipientId: conversation.otherParticipant.userId,
      content: content.trim(),
      messageType,
      threadId: activeThreadId === 'general' ? null : activeThreadId,
      relatedEntityId
    };

    try {
      await dispatch(sendMessage(messageData)).unwrap();
      scrollToBottom();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleThreadChange = (threadId) => {
    setActiveThreadId(threadId);
  };

  if (!conversation) {
    return (
      <div className="conversation-view-empty">
        <Icon name="comments outline" size="huge" />
        <Header as="h3">No conversation selected</Header>
      </div>
    );
  }

  return (
    <div className="conversation-view">
      <div className="conversation-header">
        {showBackButton && (
          <Button
            icon="arrow left"
            onClick={onBack}
            basic
            circular
            className="back-button"
          />
        )}

        <div className="conversation-title">
          <Header as="h4">
            {conversation.otherParticipant.name || conversation.otherParticipant.email}
          </Header>
          <span className="participant-email">
            {conversation.otherParticipant.email}
          </span>
        </div>
      </div>

      <div className="conversation-main">
        {threads.length > 1 && (
          <aside className="conversation-threads-pane">
            <ThreadSelector
              threads={threads}
              activeThreadId={activeThreadId}
              onThreadChange={handleThreadChange}
              orientation="vertical"
            />
          </aside>
        )}

        <div className="conversation-main-column">
          <div className="conversation-body">
            {loading.messages ? (
              <div className="conversation-loading">
                <Loader active inline="centered" />
                <p>Loading messages...</p>
              </div>
            ) : (
              <>
                <MessageThread
                  messages={currentThreadMessages}
                  currentUserId={currentUser?.userId}
                  otherParticipant={conversation.otherParticipant}
                />
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          <div className="conversation-footer">
            <MessageComposer
              onSendMessage={handleSendMessage}
              disabled={loading.sending}
              placeholder={
                conversation.otherParticipant.name && !conversation.otherParticipant.name.includes('@')
                  ? `Reply to ${conversation.otherParticipant.name}...`
                  : 'Type your reply...'
              }
              threadContext={activeThreadId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};


export default ConversationView;
